import { useState } from 'react';

import {
  useSchedules,
  useCreateSchedule,
  useUpdateSchedule,
  useDeleteSchedule,
} from '@/hooks/useSchedules';
import { useTemplates } from '@/hooks/useTemplates';
import { Loading, ErrorBanner, EmptyState } from '@/components/common/States';
import { relTime } from '@/lib/format';
import type { ScheduleKind } from '@/types';

export function Schedules() {
  const { data, isLoading, error } = useSchedules();
  const templates = useTemplates();
  const create = useCreateSchedule();
  const update = useUpdateSchedule();
  const del = useDeleteSchedule();

  const [templateId, setTemplateId] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState<ScheduleKind>('once');
  const [runAt, setRunAt] = useState(''); // datetime-local
  const [cron, setCron] = useState('0 2 * * *');
  const [formErr, setFormErr] = useState<string | null>(null);

  const tmplName = (id: string) => templates.data?.find((t) => t.id === id)?.name ?? id.slice(0, 8);

  async function submit() {
    setFormErr(null);
    if (!name.trim()) return setFormErr('Name the schedule.');
    if (!templateId) return setFormErr('Pick a template.');
    if (kind === 'once' && !runAt) return setFormErr('Pick a date/time.');
    if (kind === 'cron' && !cron.trim()) return setFormErr('Enter a cron expression.');
    try {
      await create.mutateAsync({
        name: name.trim(),
        template_id: templateId,
        kind,
        // datetime-local is local time; toISOString normalises to UTC.
        run_at: kind === 'once' ? new Date(runAt).toISOString() : null,
        cron: kind === 'cron' ? cron.trim() : null,
      });
      setName('');
      setRunAt('');
    } catch (e) {
      setFormErr(e instanceof Error ? e.message : 'Failed to create schedule.');
    }
  }

  return (
    <div className="fade-up">
      <div className="page-head">
        <div className="eyebrow">automation</div>
        <div className="title">Schedules</div>
        <div className="sub">Run a template automatically — once at a time, or on a cron.</div>
      </div>

      <div className="panel" style={{ marginBottom: 16 }}>
        <div className="eyebrow" style={{ marginBottom: 10 }}>new schedule</div>
        {!templates.data?.length ? (
          <div className="sub">Create a template first — schedules run a saved template.</div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '1 1 180px' }}>
                <label className="label">Name</label>
                <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="nightly regression" />
              </div>
              <div style={{ flex: '1 1 180px' }}>
                <label className="label">Template</label>
                <select className="input" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                  <option value="">— select —</option>
                  {templates.data.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">When</label>
                <select className="input" value={kind} onChange={(e) => setKind(e.target.value as ScheduleKind)}>
                  <option value="once">Once</option>
                  <option value="cron">Cron</option>
                </select>
              </div>
              {kind === 'once' ? (
                <div>
                  <label className="label">Date / time (local)</label>
                  <input className="input" type="datetime-local" value={runAt} onChange={(e) => setRunAt(e.target.value)} />
                </div>
              ) : (
                <div style={{ flex: '1 1 160px' }}>
                  <label className="label">Cron (UTC, 5-field)</label>
                  <input className="input" value={cron} onChange={(e) => setCron(e.target.value)} placeholder="0 2 * * *" />
                </div>
              )}
              <button className="btn btn-primary" onClick={submit} disabled={create.isPending}>
                {create.isPending ? 'Adding…' : 'Add'}
              </button>
            </div>
            {formErr && <div className="sub" style={{ color: 'var(--bad)', marginTop: 8 }}>{formErr}</div>}
          </>
        )}
      </div>

      {isLoading ? (
        <Loading label="Loading schedules" />
      ) : error ? (
        <ErrorBanner error={error} />
      ) : !data || data.length === 0 ? (
        <EmptyState title="No schedules" hint="Add one above to run a template automatically." />
      ) : (
        <div className="panel">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Template</th>
                <th>When</th>
                <th>Next run</th>
                <th>Last</th>
                <th className="right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.map((s) => (
                <tr key={s.id} style={{ opacity: s.enabled ? 1 : 0.5 }}>
                  <td>{s.name}</td>
                  <td className="muted">{tmplName(s.template_id)}</td>
                  <td className="muted">{s.kind === 'cron' ? <code>{s.cron}</code> : 'once'}</td>
                  <td className="muted">{s.next_run_at ? relTime(s.next_run_at) : s.enabled ? '—' : 'disabled'}</td>
                  <td className="muted">{s.last_status ?? '—'}</td>
                  <td className="right">
                    <button
                      className="btn"
                      onClick={() => update.mutate({ id: s.id, body: { enabled: !s.enabled } })}
                    >
                      {s.enabled ? 'Disable' : 'Enable'}
                    </button>
                    <button
                      className="btn"
                      style={{ marginLeft: 8 }}
                      onClick={() => {
                        if (confirm(`Delete schedule "${s.name}"?`)) del.mutate(s.id);
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
